// OrganizationChart.tsx
import React from 'react';
import ReactFlow, {
  BackgroundVariant,
  Background,
  Controls,
} from 'reactflow';
import 'reactflow/dist/style.css';

// Components
// import ChatWidget from '../component/ChatWidget/ChatWidget';
import Footer from '../component/Footer/FooterCommon';
import CustomNode from '../component/OrganizationCard/CustomNode';
import HeaderChart from '../component/OrganizationCard/HeaderChart';
import UserProfileSidebar from '../component/OrganizationCard/ProfileSidebar';
import { useOrganizationChart } from '../component/OrganizationCard/UseOraganizationChart';
import { UserProfile } from '../component/OrganizationCard/Types';
import { userProfiles, recentActions } from '../component/OrganizationCard/UserData';

// Node types
const nodeTypes = {
  custom: CustomNode,
};

const OrganizationChart: React.FC = () => {
  const {
    nodes,
    edges,
    isProfileOpen,
    selectedUser,
    isMobile,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onNodeClick,
    closeProfile,
    handleBackClick,
  } = useOrganizationChart();

  // Get current user data
  const currentUser: UserProfile | null = selectedUser ? userProfiles[selectedUser] : null;

  return (
    <>
      <div className="min-h-screen">
        {/* Header */}
        <HeaderChart onBackClick={handleBackClick} />

        {/* Main Chart Area */}
        <div className="w-full lg:w-full xl:w-[795px] mx-auto h-[500px] sm:h-[600px] lg:h-[542px] xl:h-[642px] relative bg-gray-50">
          <style>
            {`
            .react-flow__node.dragging {
              cursor: grabbing !important;
            }
            .react-flow__node:hover {
              cursor: grab !important;
            }
            .react-flow__node.draggable {
              cursor: grab !important;
            }
            .react-flow__node.draggable:active {
              cursor: grabbing !important;
            }
            
            /* Fix for white cursor - force black cursor */
            .react-flow__pane {
              cursor: default !important;
            }
            .react-flow__pane:active {
              cursor: grabbing !important;
            }
            .react-flow__pane.dragging {
              cursor: grabbing !important;
            }
            
            /* Ensure all cursors are black/visible */
            .react-flow__pane, 
            .react-flow__renderer, 
            .react-flow__container {
              cursor: default !important;
              color: black !important;
            }
            
            /* For grab cursor when panning */
            .react-flow__pane.react-flow__pane--pannable {
              cursor: grab !important;
            }
            .react-flow__pane.react-flow__pane--pannable:active {
              cursor: grabbing !important;
            }
            
            /* Force black grab/grabbing cursors */
            * {
              cursor: default !important;
            }
            
            .react-flow__pane:hover {
              cursor: grab !important;
            }
            
            .react-flow__pane.react-flow__pane--pannable:hover {
              cursor: grab !important;
            }
            
            /* Override any white cursor with black grab cursors */
            .react-flow__pane.react-flow__pane--pannable,
            .react-flow__pane.react-flow__pane--pannable:active,
            .react-flow__pane.react-flow__pane--pannable:hover {
              cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><path fill="black" d="M8.5 1a.5.5 0 0 0-1 0v1.5H6a.5.5 0 0 0 0 1h1.5V5a.5.5 0 0 0 1 0V3.5H10a.5.5 0 0 0 0-1H8.5V1zM3 8a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2A.5.5 0 0 1 3 10V8zm7 0a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2A.5.5 0 0 1 10 10V8z"/></svg>'), grab !important;
            }
            
            .react-flow__pane.react-flow__pane--pannable:active {
              cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><path fill="black" d="M8.5 1a.5.5 0 0 0-1 0v1.5H6a.5.5 0 0 0 0 1h1.5V5a.5.5 0 0 0 1 0V3.5H10a.5.5 0 0 0 0-1H8.5V1zM3 8a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2A.5.5 0 0 1 3 10V8zm7 0a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-2A.5.5 0 0 1 10 10V8z"/></svg>'), grabbing !important;
            }
          `}
          </style>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            className="!cursor-default"
            fitViewOptions={{
              padding: 0.15,
              includeHiddenNodes: false,
              minZoom: 0.5,
              maxZoom: 1.5,
            }}
            defaultViewport={{ x: 0, y: 0, zoom: isMobile ? 0.9 : 0.75 }}
            minZoom={0.3}
            maxZoom={2}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            zoomOnScroll={true}
          >
            <Controls
              position="top-right"
              showZoom={true}
              showFitView={true}
              showInteractive={true}  // This shows the lock toggle - by default locked
              style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '4px',
              }}
            />
            <Background
              variant={BackgroundVariant.Dots}
              gap={20}
              size={1.5}
              color="#e5e7eb"
              style={{
                backgroundColor: '#f9fafb',
              }}
            />
          </ReactFlow>
        </div>

        {/* Chat Widget */}
        {/* <ChatWidget /> */}

        {/* Profile Sidebar */}
        {currentUser && (
          <UserProfileSidebar
            isOpen={isProfileOpen}
            onClose={closeProfile}
            user={currentUser}
            recentActions={recentActions}
          />
        )}
      </div>
      <Footer />
    </>
  );
};

export default OrganizationChart;